import { compareUrls } from '@carboncopyco/editor-utils';
import { WarningOutlineIcon } from '@carboncopyco/icons';
import type {
  GuidanceTooltipSchemaType,
  Modification,
} from '@carboncopyco/types';
import {
  Badge,
  ColorHex,
  PositionedToggle,
  TextCaption,
  Tooltip,
} from '@carboncopyco/ui';
import { useMemo, useRef, useState } from 'react';
import {
  RuleCardDropdown,
  type RuleCardDropdownProps,
  RuleCardToggle,
  RulesContent,
} from '../RulesManager';
import { GuidanceTooltipsRulePreview } from './GuidanceTooltipsRulePreview';

type Rule = Modification<GuidanceTooltipSchemaType.guidanceTooltip>;
export interface GuidanceTooltipsRuleListItemProps {
  rule: Rule;
  pageUrl: string;
  onToggleVisibility: (rule: Rule, isVisible: boolean) => void;
  onDelete: (rule: Rule) => void;
  onItemHover: (rule: Rule) => void;
  onItemUnhover: (rule: Rule) => void;
  onClick: (rule: Rule) => void;
  onSetGlobal?: (edit: Rule, isGlobal: boolean) => void;
}

export const GuidanceTooltipsRuleListItem = ({
  rule,
  pageUrl,
  onDelete,
  onToggleVisibility,
  onItemHover,
  onItemUnhover,
  onClick,
  onSetGlobal,
}: GuidanceTooltipsRuleListItemProps) => {
  const [isHover, setIsHover] = useState(false);
  const tooltipRef = useRef();
  const isCurrentPageUrl = compareUrls(pageUrl, rule.content.url);
  const isGlobalRule = !!rule?.content?.isGlobal;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const dropdownItems = useMemo(
    () =>
      [
        {
          type: 'makeGlobalRule',
          onClick: () => onSetGlobal?.(rule, !isGlobalRule),
          iconRight: (
            <PositionedToggle
              checked={isGlobalRule}
              onChange={() => {
                return;
              }}
            />
          ),
        },
        {
          type: 'delete',
          onClick: () => onDelete(rule),
        },
      ] as RuleCardDropdownProps['items'],
    [rule, onDelete]
  );

  const UnactiveTooltipWarningEl = useMemo(
    () => (
      <div className="relative flex items-center">
        {!isHover && !isDropdownOpen && (
          <div
            ref={tooltipRef}
            className="w-6 h-6 rounded-full p-1.5 items-center flex
bg-ds-danger-100 cursor-pointer absolute right-1.5"
          >
            <WarningOutlineIcon color={ColorHex.danger300} />
          </div>
        )}

        <Tooltip
          placement="top-start"
          anchorRef={tooltipRef}
          noArrow
          shadow="lg"
          appendTo={() => document.body}
        >
          <div className="w-80 p-3 flex flex-col">
            <TextCaption
              label="Un active tooltip"
              color="text-ds-natural-600"
            />
            <TextCaption
              label={`The tooltip was linked to a deleted element.
    Once the element is restored from the spot edits manager,
     the tooltip will also become available again.`}
            />
          </div>
        </Tooltip>
        <RuleCardDropdown
          items={dropdownItems}
          onDropdownOpen={(isOpen) => {
            setIsDropdownOpen(isOpen);

            if (!isOpen) {
              setIsHover(false);
            }
          }}
        />
      </div>
    ),
    [dropdownItems, isHover, isDropdownOpen]
  );

  const action = useMemo(() => {
    if (rule.content.isParentElementDeleted) {
      return UnactiveTooltipWarningEl;
    }

    if (!rule.isActive) {
      return (
        <div className="relative flex items-center">
          <RuleCardToggle
            testId="toggle-guidance-tooltip-visibility"
            checked={rule.isActive}
            onChange={() => {
              rule.isActive
                ? onToggleVisibility(rule, false)
                : onToggleVisibility(rule, true);
            }}
          />
          {!isHover && !isDropdownOpen && (
            <div className="absolute right-0">
              <Badge
                label="OFF"
                variant="disabled"
              />
            </div>
          )}
          <RuleCardDropdown
            items={dropdownItems}
            onDropdownOpen={(isOpen) => {
              setIsDropdownOpen(isOpen);

              if (!isOpen) {
                setIsHover(false);
              }
            }}
          />
        </div>
      );
    }

    if (rule.isActive) {
      return (
        <>
          <RuleCardToggle
            testId="toggle-guidance-tooltip-visibility"
            checked={rule.isActive}
            onChange={() => {
              rule.isActive
                ? onToggleVisibility(rule, false)
                : onToggleVisibility(rule, true);
            }}
          />
          <RuleCardDropdown items={dropdownItems} />
        </>
      );
    }

    return null;
  }, [
    rule,
    UnactiveTooltipWarningEl,
    isHover,
    dropdownItems,
    isDropdownOpen,
    onToggleVisibility,
  ]);

  return (
    <RulesContent.List.Item
      expandable={false}
      id={rule.id}
      header={
        <GuidanceTooltipsRulePreview
          rule={rule}
          isDisabled={!rule.isActive || rule.content.isParentElementDeleted}
        />
      }
      action={action}
      onMouseEnter={() => {
        setIsHover(true);
        if (isCurrentPageUrl) {
          onItemHover(rule);
        }
      }}
      onMouseLeave={() => {
        setIsHover(false);
        if (isCurrentPageUrl) {
          onItemUnhover(rule);
        }
      }}
      onClick={() => onClick(rule)}
    />
  );
};
